import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LuCalendarCheck,
  LuChartColumn,
  LuContact,
  LuFileText,
  LuFiles,
  LuGraduationCap,
  LuHouse,
  LuNotebookPen,
  LuReceiptIndianRupee,
  LuSparkles,
  LuUsers,
} from 'react-icons/lu';
import { isFeatureEnabled } from '../../config/appConfig';

/**
 * The main menu, always visible on the left. Every item has an icon and a plain-word label,
 * grouped under short headings so people can find a screen without hunting.
 * Folded (`isCollapsed`), it shows only the icons; the label appears as a tooltip.
 */
// The time-table and settings routes still show sample data, so they are not in the menu.
const SidebarNav = ({ schoolId, isCollapsed }) => {
  const location = useLocation();
  const base = `/dash/${schoolId}`;

  const sections = [
    {
      heading: '',
      links: [
        { to: base, label: 'Home', icon: LuHouse, isShown: true },
        { to: `${base}/ask`, label: 'Ask Me', icon: LuSparkles, isShown: true },
      ],
    },
    {
      heading: 'People',
      links: [
        { to: `${base}/students`, label: 'Students', icon: LuUsers, isShown: true },
        { to: `${base}/staff`, label: 'Staff', icon: LuContact, isShown: true },
      ],
    },
    {
      heading: 'Daily work',
      links: [
        { to: `${base}/attendance`, label: 'Attendance', icon: LuCalendarCheck, isShown: true },
        { to: `${base}/fees`, label: 'Fees', icon: LuReceiptIndianRupee, isShown: true },
      ],
    },
    {
      heading: 'Reports',
      links: [
        { to: `${base}/reports/lesson-plans`, label: 'Subject reports', icon: LuNotebookPen, isShown: true },
        { to: `${base}/student-report`, label: 'Student reports', icon: LuGraduationCap, isShown: true },
        { to: `${base}/reports/class`, label: 'Class reports', icon: LuChartColumn, isShown: true },
        { to: `${base}/bulk-reports`, label: 'Bulk reports', icon: LuFiles, isShown: isFeatureEnabled('bulkReports') },
        { to: `${base}/hpc-report`, label: 'HPC reports', icon: LuFileText, isShown: isFeatureEnabled('hpcReport') },
      ],
    },
  ];

  // The link whose path is the longest prefix of the current path is the active one,
  // so /students/123 highlights "Students" and /reports/class/... highlights "Class reports".
  const allLinks = sections.flatMap((section) => section.links);
  const activePath = allLinks
    .map((link) => link.to)
    .filter((path) => {
      if (path === base) {
        return location.pathname === base || location.pathname === `${base}/`;
      }
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    })
    .sort((firstPath, secondPath) => secondPath.length - firstPath.length)[0];

  const renderLink = (link) => {
    const Icon = link.icon;
    const isActive = link.to === activePath;

    return (
      <Link
        key={link.to}
        to={link.to}
        title={isCollapsed ? link.label : undefined}
        aria-current={isActive ? 'page' : undefined}
        className={`relative flex h-9 items-center gap-3 rounded-lg text-sm font-medium transition-colors ${
          isCollapsed ? 'justify-center px-0' : 'px-2.5'
        } ${
          isActive
            ? 'bg-ui-brand-soft text-ui-brand-soft-ink'
            : 'text-ui-ink-2 hover:bg-ui-hover hover:text-ui-ink'
        }`}
      >
        {isActive && !isCollapsed && (
          <span aria-hidden className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full bg-brand-500" />
        )}
        <Icon aria-hidden className={`size-[18px] shrink-0 ${isActive ? 'text-brand-600 dark:text-brand-300' : 'text-ui-ink-3'}`} />
        {!isCollapsed && <span className="truncate">{link.label}</span>}
      </Link>
    );
  };

  return (
    <nav aria-label="Main menu" className="flex h-full flex-col">
      <div className={`ui-scroll flex-1 overflow-x-hidden overflow-y-auto py-3 ${isCollapsed ? 'px-2' : 'px-3'}`}>
        {sections.map((section, sectionIndex) => {
          const visibleLinks = section.links.filter((link) => link.isShown);
          if (visibleLinks.length === 0) {
            return null;
          }

          return (
            <div key={section.heading || sectionIndex} className={sectionIndex > 0 ? 'mt-4' : ''}>
              {section.heading && !isCollapsed && (
                <p className="mb-1 px-2.5 text-[11px] font-semibold tracking-wider text-ui-ink-3 uppercase">
                  {section.heading}
                </p>
              )}
              {section.heading && isCollapsed && <div aria-hidden className="mx-2 mb-2 border-t border-ui-line-soft" />}
              <div className="flex flex-col gap-0.5">{visibleLinks.map(renderLink)}</div>
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default SidebarNav;
